import { Button } from "@volca/ui/components/ui/button";
import {
  TOOL_PEGBOARD,
  WATER_HEATER,
} from "@volca/ui/components/ui/empty-machine";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { canManageReport } from "@/profitability/access";
import type { RecordObject } from "@/profitability/records/objects";
import { ReportWorkbook } from "@/profitability/report-workbook";
import type { ProfitabilityReport, ReportOutput } from "@/profitability/types";
import { useRerunReport } from "@/profitability/use-rerun-report";
import { reportError } from "@/profitability/utils";
import { useEmptyActions } from "./use-empty-actions";
import type { usePageEdge } from "./use-page-edge";
import type { TemplatePeriod } from "./use-template-period";

/**
 * The Workbook tab: the period's generated workbook, or why there is none
 * yet, each with the step that fills it (`useEmptyActions`): running a
 * period, following the first period as it builds, or reprocessing a period
 * whose workbook is gone. Someone who may not take
 * the step reads who can.
 */
export const TemplateWorkbook = ({
  businessId,
  report,
  period,
  output,
  objects,
  edge,
}: {
  businessId: string;
  report: ProfitabilityReport;
  period: TemplatePeriod;
  output: ReportOutput | undefined;
  objects: RecordObject[];
  edge: ReturnType<typeof usePageEdge>;
}) => {
  const { run, overview } = period;
  const { rerun, rerunning } = useRerunReport(businessId, run);
  const actions = useEmptyActions(report, overview.data?.running);

  if (overview.isError)
    return (
      <ErrorState
        title="The workbook could not load"
        description={reportError(overview.error)}
        retrying={overview.isFetching}
      >
        <Button onClick={() => void overview.refetch()}>Try again</Button>
      </ErrorState>
    );
  if (overview.isPending || period.periodChanging)
    return (
      <Stack
        gap={2}
        align="stretch"
        className="min-h-112 w-full flex-1 rounded-xl border border-line p-3"
        aria-busy
        role="status"
      >
        <span className="sr-only">Loading the workbook</span>
        <Skeleton className="h-8 w-full" aria-hidden />
        {["one", "two", "three", "four", "five", "six", "seven", "eight"].map(
          (row) => (
            <Skeleton key={row} className="h-6 w-full" aria-hidden />
          ),
        )}
      </Stack>
    );
  if (run?.workbook_available)
    return (
      <Stack gap={0} align="stretch" className="min-h-112 w-full flex-1">
        <ReportWorkbook
          key={run.id}
          businessId={businessId}
          runId={run.id}
          onAskAiAboutCell={edge.askAboutCell}
          output={output}
          objects={objects}
          onOpenRecord={edge.openRecord}
        />
      </Stack>
    );
  if (run) {
    const canRerun =
      canManageReport(report) &&
      !report.archived_at &&
      report.build_status !== "building" &&
      !run.draft_sample;
    return (
      <MachineEmptyState
        art={TOOL_PEGBOARD}
        layout="workspace"
        title="This workbook is no longer available"
        description={
          canRerun
            ? "Reprocess the period to build a fresh copy."
            : "Someone who manages this agent can reprocess the period to build a fresh copy."
        }
      >
        {canRerun && (
          <Button
            loading={rerunning}
            loadingLabel="Starting the period"
            onClick={() => rerun()}
          >
            Reprocess with current settings
          </Button>
        )}
      </MachineEmptyState>
    );
  }
  if (overview.data.running)
    return (
      <Stack role="status" align="stretch" className="w-full flex-1">
        <MachineEmptyState
          art={WATER_HEATER}
          layout="workspace"
          active
          title="The first workbook is building"
          description="It opens here when the first period finishes. You can leave this page."
        >
          {actions.progress && (
            <Button variant="secondary" onClick={actions.progress}>
              See progress
            </Button>
          )}
        </MachineEmptyState>
      </Stack>
    );
  return (
    <MachineEmptyState
      art={TOOL_PEGBOARD}
      layout="workspace"
      title="No workbook yet"
      description={
        report.published
          ? actions.run
            ? "Each period builds a workbook of its figures. Run a period and it opens here."
            : "Each period builds a workbook of its figures. It opens here once someone who manages this agent runs one."
          : "This agent is still being set up. Once it runs a period, the period's workbook opens here."
      }
    >
      {actions.run && <Button onClick={actions.run}>Run a period</Button>}
    </MachineEmptyState>
  );
};
